import { z } from 'zod';
import { NEED_ID_PATTERN } from './needRows';

// The buying board's own URL state; the transfer board reads the same keys.
export const buyingBoardSearchSchema = z.object({
  // `divisionIds` / `warehouseIds` come from the parent procurement filter
  // route (arrays; the old comma-separated form is still read there).
  // Legacy single-division param. Still accepted for backward-compatible
  // links; the page normalizes it into the division list.
  divisionId: z.uuid().optional(),
  // When true, hide supplier cards with no open sales-order lines (deficit-only
  // suppliers) so the buyer can focus on must-act demand.
  soOnly: z.boolean().optional(),
  // When true, reveal trashed (rejected) suggestions across the board so the
  // buyer can review + restore them.
  showTrashed: z.boolean().optional(),
  // When true, hide lines released since the last refresh (shown by default).
  hideReleased: z.boolean().optional(),
  supplierId: z.uuid().optional(),
  suggestionId: z.uuid().optional(),
  // The cart line shown in the board's Line section: a suggestion id, or a
  // need row's id (GEN-2938) for an item's SO lines folded under one row.
  line: z.union([z.uuid(), z.string().regex(NEED_ID_PATTERN)]).optional(),
  // The Line section's open tab (GEN-3285); unknown values open At a glance.
  lineTab: z.string().optional(),
  // Master-table sort. Used to carry the old belt sort stack; unknown values
  // fall back to the default ranking.
  sort: z.string().optional(),
  sortDir: z.enum(['asc', 'desc']).optional().catch(undefined),
  // 1-based page of the buying-orders table (100 per page).
  page: z.number().int().min(1).optional().catch(undefined),
  // Comma-separated line-type filter (back_order,potf,stock); absent = all.
  lineTypes: z.string().optional(),
  // One row per vendor across ship-tos ('vendor') instead of per cart.
  groupBy: z.enum(['vendor_location', 'vendor']).optional().catch(undefined),
  // Vendor filter on the buying-orders table.
  vendorIds: z.array(z.uuid()).optional().catch(undefined),
  // Buyer-code filter (GAINS Receiving Planner) on the buying-orders table.
  buyers: z.array(z.string()).optional().catch(undefined),
  // Order-readiness filter (the Order column's states).
  ready: z
    .array(z.enum(['ready', 'warning', 'not_ready']))
    .optional()
    .catch(undefined),
  // Days-on-order filter: orders holding a line at least this old.
  minAge: z.number().int().min(0).optional().catch(undefined),
  // Comma-separated cart-view chip filters (e.g. ?chips=sos,below_mov).
  // Shared between AssignmentBoard's toolbar and SuggestionDetailModal's
  // prev/next sibling list so both honor the same filter.
  chips: z.string().optional(),
  // Cart-view free-text search across SKU / name / warehouse.
  q: z.string().optional(),
  // Selected ship-to (PO cart) within the flat supplier table. When set, the
  // AssignmentBoard table is scoped to that cart; absent = show all carts.
  shipTo: z.uuid().optional(),
  // When true, the synthetic "Vendorless transfers" bucket is selected
  // (mutually exclusive with supplierId) — drills into the transfers list.
  vendorless: z.boolean().optional(),
});
